import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Box, Fade, useTheme } from '@mui/material';
import { Check, SwipeRounded } from '@mui/icons-material';
import { preventDefault, useBox, useCallbacks } from 'shared/hooks/utils';
import { Sector } from './sector';
import { DashedSector } from './dashed-sector';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { useObserveControl } from 'console/shared/contexts/observe-control';
import { useHeatingCircuit } from 'console/shared/components/panels/heating-circuits/heating-circuit/provider';
import { useHeatingCircuitControl } from 'console/shared/components/panels/heating-circuits/heating-circuit/provider/control';
import { useBooleanState } from 'shared/hooks/state/boolean';
function createRoundedTrianglePath(x, y, width) {
    const x_plus_width = x + width;
    const y_plus_width = y + width;
    // _______
    // \     /
    //  \   /
    //   \ /
    return (`M ${x_plus_width} ${y_plus_width}` + // точка старта
        `L ${x_plus_width} ${y - width} ` + //верхняя грань
        `L ${x - width} ${y}` + // левая грань
        `L ${x_plus_width} ${y_plus_width} z` // правая грань
    );
}
function roundToPrecision(value, step) {
    return Math.round(value * step) / step;
}
export const centerX = 60;
export const centerY = 60;
export const scaleRadius = 55;
// export const actualMarkLength = 8;
// export const directionArcWidth = 5;
export const scaleMinAngle = (130 * Math.PI) / 180;
export const scaleMaxAngle = (410 * Math.PI) / 180;
export const maxX = Math.cos(scaleMaxAngle) * scaleRadius + centerX;
export const maxY = Math.sin(scaleMaxAngle) * scaleRadius + centerY;
export const minX = Math.cos(scaleMinAngle) * scaleRadius + centerX;
export const minY = Math.sin(scaleMinAngle) * scaleRadius + centerY;
export const FancyTemperatureControl = memo(function FancyTemperatureControl(props) {
    const { is_off_mode, is_alarm, disable } = props;
    const { temp_step } = useDeviceConfig();
    const { disableModalInteraction, enableModalInteraction, control_disabled } = useObserveControl();
    const { is_manual_mode, temperature_decimal_places, min_temperature, max_temperature, thermometers: sensors, has_pza, is_air_pid, is_air, summer_mode, } = useHeatingCircuit();
    const { setTargetTemperature, turnOnEditingMode, wait_apply_command, success_apply_command, stopShowSuccess, editing_mode, target_temperature, target_fail, } = useHeatingCircuitControl();
    const current_temperature = useMemo(() => sensors?.target.temperature, [sensors?.target.temperature]);
    //Предыдущее значение температуры, нужно для корректной работы таскалки
    const [prev_target_temperature, setPrevTargetTemperatue] = useState(target_temperature);
    const theme = useTheme();
    const _min = useMemo(() => (min_temperature != null ? min_temperature : 0), [min_temperature]);
    const _max = useMemo(() => (max_temperature != null ? max_temperature : 0), [max_temperature]);
    const _actual = useMemo(() => current_temperature ?? 0, [current_temperature]);
    const _target = useMemo(() => target_temperature ?? _actual, [target_temperature, _actual]);
    const _prev_target = useMemo(() => prev_target_temperature ?? 0, [prev_target_temperature]);
    const targetAngle = useMemo(() => ((_target - _min) / (_max - _min)) * (scaleMaxAngle - scaleMinAngle) +
        scaleMinAngle, [_target, _min, _max]);
    const targetX = useMemo(() => Math.cos(targetAngle) * scaleRadius + centerX, [targetAngle]);
    const targetY = useMemo(() => Math.sin(targetAngle) * scaleRadius + centerY, [targetAngle]);
    const actualAngle = useMemo(() => {
        const actual = ((_actual - _min) / (_max - _min)) * (scaleMaxAngle - scaleMinAngle) +
            scaleMinAngle;
        return actual > scaleMaxAngle
            ? scaleMaxAngle
            : actual < scaleMinAngle
                ? scaleMinAngle
                : actual;
    }, [_actual, _min, _max]);
    const actualNearX = useMemo(() => Math.cos(actualAngle) * scaleRadius + centerX, [actualAngle]);
    const actualNearY = useMemo(() => Math.sin(actualAngle) * scaleRadius + centerY, [actualAngle]);
    const svgRef = useRef(null);
    const step = useMemo(() => 1 / temp_step, [temp_step]);
    const onPointerMove = useCallback((pointerX, pointerY) => {
        if (control_disabled) {
            return;
        }
        if (!svgRef.current) {
            return;
        }
        const screenCTM = svgRef.current.getScreenCTM();
        if (!screenCTM) {
            return;
        }
        const point = svgRef.current.createSVGPoint();
        [point.x, point.y] = [pointerX, pointerY];
        const { x, y } = point.matrixTransform(screenCTM.inverse());
        let newAngle = Math.atan2(y - centerY, x - centerX);
        while (newAngle < Math.PI / 2) {
            newAngle += 2 * Math.PI;
        }
        const temp = ((newAngle - scaleMinAngle) / (scaleMaxAngle - scaleMinAngle)) *
            (_max - _min) +
            _min;
        const clamped = temp < _min ? _min : temp > _max ? _max : temp;
        setTargetTemperature(roundToPrecision(clamped, step));
    }, [svgRef.current, setTargetTemperature, _max, _min, _actual, control_disabled]);
    useEffect(() => {
        if (editing_mode) {
            if (roundToPrecision(_prev_target, step) != roundToPrecision(_target, step)) {
                navigator.vibrate?.(50);
                // audio.pause();
                // audio.currentTime = 0;
                // audio.play().then(noop);
            }
        }
        setPrevTargetTemperatue(_target);
    }, [
        editing_mode,
        _target,
        _prev_target,
        setPrevTargetTemperatue,
        step /*, audio*/,
    ]);
    const onTouchMove = useCallback((event) => onPointerMove(event.pageX, event.pageY), [onPointerMove]);
    //нужно, чтобы не учитывать нажатия кнопок +/-
    const [local_editing] = useBooleanState(editing_mode);
    useEffect(() => {
        if (local_editing) {
            const onmove = (ev) => {
                preventDefault(ev);
                onPointerMove(ev.pageX, ev.pageY);
            };
            window.addEventListener('mousemove', onmove, { passive: false });
            return () => {
                window.removeEventListener('mousemove', onmove);
            };
        }
        return undefined;
    }, [local_editing, onPointerMove]);
    const onFinishAnimation = useCallback(() => stopShowSuccess(), [stopShowSuccess]);
    // console.group(id);
    // console.log('_target', _target);
    // console.log('_min', _min);
    // console.log('_max', _max);
    // console.log('_actual', _actual);
    // console.log('actualAngle', actualAngle);
    // console.log('actualFarX', actualFarX);
    // console.log('targetX', targetX);
    // console.log('actualFarY', actualFarY);
    // console.log('targetY', targetY);
    // console.log('scaleMinAngle', scaleMinAngle);
    // console.log('scaleMaxAngle', scaleMaxAngle);
    // console.log('modulation_level', modulation_level);
    // console.log('mod_level_cond', modulation_level !== null);
    // console.log('flame', flame);
    // console.log('is_off_mode', is_off_mode);
    // console.groupEnd();
    //Считаем координаты кружочка за который изменяем температуру
    const _targetX = useMemo(() => (_target > _max ? maxX : _target < _min ? minX : targetX), [_target, _max, _min, targetX]);
    const _targetY = useMemo(() => (_target > _max ? maxY : _target < _min ? minY : targetY), [_target, _max, _min, targetY]);
    const [animation_target_angle, setAnimationTargetAngle] = useState(scaleMinAngle);
    //Завожу интервал, по которому буду увеличивать температуру
    const [click_interval, setClickInterval] = useState(null);
    const boxedInterval = useBox(click_interval);
    const changeAngle = useCallback(() => {
        setAnimationTargetAngle((angle) => {
            if (angle > targetAngle) {
                return scaleMinAngle;
            }
            return angle + 0.1;
        });
    }, [setAnimationTargetAngle, targetAngle, scaleMinAngle]);
    useEffect(() => {
        if (wait_apply_command) {
            if (!boxedInterval.current) {
                setClickInterval(window.setInterval(changeAngle, 75));
            }
        }
        else {
            if (boxedInterval.current) {
                window.clearInterval(boxedInterval.current);
                setClickInterval(null);
            }
        }
        return () => {
            if (boxedInterval.current) {
                window.clearInterval(boxedInterval.current);
                setClickInterval(null);
            }
        };
    }, [wait_apply_command]);
    const onInteractWithWidget = useCallbacks([
        preventDefault,
        turnOnEditingMode,
        disableModalInteraction,
    ]);
    const show_current_temperature = useMemo(() => !has_pza || is_air_pid || is_air, [has_pza, is_air_pid, is_air]);
    const [showFail, setShowFail] = useState(false);
    useEffect(() => {
        if (target_fail) {
            setShowFail(true);
            setTimeout(() => setShowFail(false), 4000);
        }
    }, [target_fail]);
    return (_jsx(Box, { className: "fancy-temp-control", children: _jsxs("svg", { viewBox: "0 0 120 120", width: "100%", height: "100%", ref: svgRef, children: [_jsx(Sector, { cx: centerX, cy: centerY, rx: scaleRadius, ry: scaleRadius, startRad: scaleMinAngle, endRad: scaleMaxAngle, stroke: showFail
                        ? theme.palette.error.main
                        : is_off_mode || summer_mode
                            ? theme.palette.action.disabled
                            : !is_alarm
                                ? theme.palette.heat.main
                                : theme.palette.error.main, strokeWidth: 1, fill: "none" }), _jsx(DashedSector, { cx: centerX, cy: centerY, rx: scaleRadius - 12, ry: scaleRadius - 12, startRad: scaleMinAngle, endRad: scaleMaxAngle, stroke: theme.palette.text.disabled, fill: "none" }), !is_alarm && !is_off_mode && !summer_mode && (_jsx(DashedSector, { className: "dashed-sector-target-temp", cx: centerX, cy: centerY, rx: scaleRadius - 12, ry: scaleRadius - 12, startRad: scaleMinAngle, endRad: wait_apply_command ? animation_target_angle : targetAngle, stroke: showFail
                        ? theme.palette.error.main
                        : theme.palette.heat.main, fill: "none" })), !is_alarm && !is_off_mode && !summer_mode && (_jsx("text", { x: centerX, y: centerY - 22, textAnchor: "middle", dominantBaseline: "middle", style: { fontSize: '6px', fontWeight: 400 }, fill: "#777", children: show_current_temperature
                        ? targetAngle >= actualAngle
                            ? 'Нагрев t° до:'
                            : 'Снижение t° до:'
                        : '' })), _jsx(Fade, { in: !editing_mode && is_off_mode, children: _jsx("text", { x: centerX, y: summer_mode ? centerY - 7 : centerY, textAnchor: "middle", dominantBaseline: "middle", style: {
                            whiteSpace: 'pre-line',
                            fontSize: '14px',
                        }, fill: theme.palette.action.disabled, children: "\u041E\u0442\u043A\u043B\u044E\u0447\u0451\u043D" }) }), _jsx(Fade, { in: !is_off_mode && summer_mode, children: _jsx("text", { x: centerX, y: centerY - 22, textAnchor: "middle", dominantBaseline: "middle", style: {
                            whiteSpace: 'pre-line',
                            fontSize: '8px',
                        }, fill: theme.palette.action.disabled, children: "\u041B\u0435\u0442\u043D\u0438\u0439 \u0440\u0435\u0436\u0438\u043C" }) }), _jsx(Fade, { in: editing_mode, timeout: 250, children: _jsx("text", { x: centerX + 6, y: centerY, textAnchor: "middle", dominantBaseline: "middle", style: {
                            fontSize: '30px',
                            fontWeight: 400,
                        }, fill: summer_mode
                            ? theme.palette.action.disabled
                            : editing_mode
                                ? theme.palette.heat.dark
                                : theme.palette.heat.main, children: roundToPrecision(_target, step).toFixed(temperature_decimal_places) + '°' }) }), _jsx(Fade, { in: !editing_mode && !is_off_mode, timeout: 250, children: _jsx("text", { x: centerX + 6, y: centerY, textAnchor: "middle", dominantBaseline: "middle", style: {
                            fontSize: '30px',
                            fontWeight: 400,
                        }, fill: showFail
                            ? theme.palette.error.main
                            : summer_mode
                                ? theme.palette.action.disabled
                                : is_alarm || disable
                                    ? theme.palette.text.secondary
                                    : editing_mode
                                        ? theme.palette.heat.dark
                                        : theme.palette.heat.main, children: roundToPrecision(_target, step).toFixed(temperature_decimal_places) + '°' }) }), !is_off_mode && (_jsxs(_Fragment, { children: [_jsx("path", { d: createRoundedTrianglePath(_targetX, _targetY, 2.5), transform: `rotate(${(targetAngle * 180) / Math.PI}, ${_targetX}, ${_targetY})`, stroke: showFail
                                ? theme.palette.error.main
                                : !is_alarm
                                    ? theme.palette.heat.main
                                    : theme.palette.error.main, strokeWidth: 4, strokeLinejoin: "round", fill: theme.palette.background.paper, onPointerDown: onInteractWithWidget, onPointerUp: enableModalInteraction, onContextMenu: preventDefault, onPointerMove: onTouchMove, "data-tooltip": "fancy-change-temp" }), _jsx("path", { d: createRoundedTrianglePath(_targetX, _targetY, 2.5), transform: `rotate(${(targetAngle * 180) / Math.PI}, ${_targetX}, ${_targetY})`, stroke: theme.palette.background.paper, strokeWidth: 2, strokeLinejoin: "round", fill: theme.palette.background.paper, onPointerDown: onInteractWithWidget, onPointerUp: enableModalInteraction, onContextMenu: preventDefault, onPointerMove: onTouchMove })] })), _actual != null &&
                    !is_off_mode &&
                    !is_alarm &&
                    !disable &&
                    show_current_temperature && (_jsx("path", { d: createRoundedTrianglePath(actualNearX, actualNearY, 2.5), transform: `rotate(${(actualAngle * 180) / Math.PI}, ${actualNearX}, ${actualNearY})`, stroke: !is_alarm
                        ? theme.palette.success.main
                        : theme.palette.error.main, strokeWidth: 2, strokeLinejoin: "round", fill: theme.palette.success.main, style: {
                        pointerEvents: 'none',
                    } })), _jsx(Fade, { in: success_apply_command, onEntered: onFinishAnimation, timeout: 1e3, children: _jsx(Check, { x: centerX - 6, y: centerY - 40, pointerEvents: "none", color: summer_mode ? 'disabled' : 'success', width: 16, height: 16 }) }), _jsx(Fade, { in: is_manual_mode && !is_off_mode && !disable, children: _jsx("g", { transform: `translate(${centerX - 6} ${centerY - 6 + 19})`, children: _jsx(SwipeRounded, { width: 12, height: 12, color: "action" }) }) }), _jsx(Fade, { in: min_temperature != null, children: _jsx("text", { x: centerX - 35, y: centerY + 51, textAnchor: "middle", dominantBaseline: "middle", style: { fontSize: '6px', fontWeight: 700 }, fill: summer_mode
                            ? theme.palette.action.disabled
                            : is_off_mode
                                ? theme.palette.action.disabled
                                : !is_alarm
                                    ? theme.palette.heat.dark
                                    : theme.palette.error.main, children: _min + '°' }) }), _jsx(Fade, { in: max_temperature != null, children: _jsx("text", { x: centerX + 35, y: centerY + 51, textAnchor: "middle", dominantBaseline: "middle", style: { fontSize: '6px', fontWeight: 700 }, fill: summer_mode
                            ? theme.palette.action.disabled
                            : is_off_mode
                                ? theme.palette.action.disabled
                                : !is_alarm
                                    ? theme.palette.heat.dark
                                    : theme.palette.error.main, children: _max + '°' }) })] }) }));
});
